import { useEffect, useMemo, useState } from 'react';

import { useNavigationStack } from '../../../../context/NavigationStackContext';
import {
  useDeleteExercise,
  useGetPublicCompactExercises,
  useReplaceExerciseWithPublic,
} from '../../../../redux/api/exercisesAPI';
import { useGetLanguages } from '../../../../redux/api/translationsAPI';
import { Paths } from '../../../../shared/paths';
import { IExercise } from '../../../../types/interfaces/exercise';

export function useExercisePage(exercise?: IExercise) {
  const [deleteExercise, { isSuccess: isDeleteSuccess }] = useDeleteExercise();
  const [replaceExerciseWithPublic, { isLoading: isReplacingExercise }] =
    useReplaceExerciseWithPublic();
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [showReplaceConfirmation, setShowReplaceConfirmation] =
    useState<boolean>(false);
  const [replacementExerciseId, setReplacementExerciseId] =
    useState<string>('');
  const { navigate } = useNavigationStack();
  const { data: languages } = useGetLanguages();
  const { data: publicExercises } = useGetPublicCompactExercises();
  const exerciseId = exercise?.id;
  const replacementExerciseOptions = useMemo(
    () =>
      publicExercises?.filter(
        (publicExercise) => publicExercise.id !== exerciseId,
      ) ?? [],
    [exerciseId, publicExercises],
  );

  useEffect(() => {
    if (isDeleteSuccess) {
      navigate(Paths.exercises.toList);
    }
  }, [isDeleteSuccess]);

  useEffect(() => {
    if (!showReplaceConfirmation) {
      setReplacementExerciseId('');
    }
  }, [showReplaceConfirmation]);

  function onEditClick() {
    if (!exerciseId) {
      return;
    }

    navigate(Paths.exercises.toEditId(exerciseId));
  }

  function onDelete() {
    if (!exerciseId) {
      return;
    }

    deleteExercise(exerciseId);
  }

  function onUserClick(userId: string) {
    navigate(Paths.users.toUser(userId));
  }

  async function onReplaceExercise() {
    if (isReplacingExercise || !replacementExerciseId || !exerciseId) {
      return;
    }

    const response = await replaceExerciseWithPublic({
      id: exerciseId,
      body: { replacementExerciseId },
    }).unwrap();

    if (response) {
      setShowReplaceConfirmation(false);
      navigate(Paths.exercises.toSummaryId(replacementExerciseId), {
        replace: true,
      });
    }
  }

  return {
    onUserClick,
    onEditClick,
    onDelete,
    onReplaceExercise,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    showReplaceConfirmation,
    setShowReplaceConfirmation,
    replacementExerciseId,
    setReplacementExerciseId,
    replacementExerciseOptions,
    isReplacingExercise,
    exerciseLanguage: languages?.find((l) => l.id === exercise?.languageId)
      ?.title,
  };
}
